// src/pages/TermsPage.jsx
import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";

export default function TermsPage({ language = "en" }) {
  const navigate = useNavigate();

  const t = {
    en: {
      title: "Accessibility Statement — YourLostAndFound",
      desc: "Accessibility Statement for YourLostAndFound (pre-launch).",
      h1: "Accessibility Statement — Pre-Launch",
      updated: "Last updated: November 2025",
      body: [
        "At YourLostAndFound, we are committed to making our website accessible and easy to use for everyone, regardless of ability or device.",
        "Our current efforts. As part of this commitment, we’ve implemented features such as Accessible Mode (available in the footer), which adjusts contrast, text size, and reduces motion to enhance readability and comfort.",
        "An ongoing effort. We recognize that accessibility is a journey, not a destination. Our site is currently in a pre-launch phase, and we are continuously working to improve the experience for all visitors in alignment with recognized accessibility standards (WCAG 2.1).",
        "Third-party tools. Some features on this site may rely on third-party components. We assess them for accessibility and, where needed, work to provide accessible alternatives or equivalent experiences. If any third-party feature creates a barrier, please let us know at accessibility@yourlostandfound.com",
		"We welcome your feedback. If you experience difficulty viewing or navigating our website, or if you notice any feature that may not be fully accessible, please contact us. Your feedback is vital to our continued improvement.",
		" ",
		"Email: accessibility@yourlostandfound.com Subject: Accessibility Feedback"
       
      ]
    },
    es: {
      title: "Declaración de Accesibilidad — YourLostAndFound",
      desc: "Declaración de Accesibilidad en pre-lanzamiento.",
      h1: "Declaración de Accesibilidad — Pre-lanzamiento",
      updated: "Última actualización: Noviembre de 2025",
      body: [
        "En YourLostAndFound, estamos comprometidos con ofrecer un sitio web accesible y fácil de usar para todas las personas, independientemente de sus capacidades o dispositivos.",
        "Nuestros esfuerzos actuales. Como parte de este compromiso, hemos implementado funciones como el Modo Accesible (disponible en el pie de página), que ajusta el contraste, el tamaño del texto y reduce las animaciones para mejorar la legibilidad y la comodidad de navegación.",
		"Un esfuerzo continuo. Reconocemos que la accesibilidad es un proceso en constante evolución. Nuestro sitio se encuentra actualmente en fase de pre-lanzamiento, y seguimos trabajando activamente para mejorar la experiencia de todos nuestros visitantes conforme a las mejores prácticas de accesibilidad web (WCAG 2.1).",
        "Herramientas de terceros. Algunas funciones de este sitio pueden depender de componentes de terceros. Evaluamos su accesibilidad y, cuando es necesario, trabajamos para ofrecer alternativas accesibles o experiencias equivalentes. Si alguna función de terceros genera una barrera, por favor escríbenos a accessibility@yourlostandfound.com",
		"¡Queremos tu opinión! Si tienes dificultades para acceder, ver o navegar por el contenido de este sitio web, o identificas alguna funcionalidad que consideres no totalmente accesible, por favor contáctanos. Tu retroalimentación es esencial para ayudarnos a mejorar.",
        " ",
        "Correo: accessibility@yourlostandfound.com. Asunto sugerido: Comentarios sobre la Accesibilidad"
       
      ]
    },
    pt: {
      title: "Declaração de Acessibilidade — YourLostAndFound",
      desc: "Declaração de Acessibilidade em pré-lançamento.",
      h1: "Declaração de Acessibilidade — Pré-lançamento",
      updated: "Última atualização: Novembro de 2025",
      body: [
        "Na YourLostAndFound, temos o compromisso de tornar nosso site acessível e fácil de usar para todas as pessoas, independentemente de suas habilidades ou dispositivos.",
        "Nossos esforços atuais. Como parte desse compromisso, implementamos recursos como o Modo Acessível (disponível no rodapé), que ajusta o contraste, o tamanho do texto e reduz as animações para melhorar a legibilidade e a experiência de navegação.",
        "Um esforço contínuo. Reconhecemos que a acessibilidade é um processo contínuo. Nosso site está em fase de pré-lançamento e continuamos trabalhando para melhorar a experiência de todos os visitantes de acordo com as melhores práticas de acessibilidade (WCAG 2.1).",
        "Ferramentas de terceiros. Alguns recursos deste site podem depender de componentes de terceiros. Avaliamos sua acessibilidade e, quando necessário, trabalhamos para oferecer alternativas acessíveis ou experiências equivalentes. Se algum recurso de terceiros gerar uma barreira, avise-nos em accessibility@yourlostandfound.com",
		"Queremos ouvir você! Se tiver dificuldades para visualizar ou navegar pelo conteúdo deste site, ou perceber algum recurso que não seja totalmente acessível, entre em contato conosco. Seu feedback é fundamental para o nosso aprimoramento contínuo.",
		" ",
		"Email: accessibility@yourlostandfound.com Subject: Accessibility Feedback"
        
      ]
    }
  }[language];

  const origin = typeof window !== "undefined" ? window.location.origin : "https://yourlostandfound.com";
  const canonical = `${origin}/terms`;

  return (
    <>
      {/* Header igual a Contact */}
      <section
        id="terms"
        className="bg-[#48887B] py-12 text-center scroll-mt-[120px] pt-[120px]"
      >
        <h2 className="text-white text-3xl md:text-5xl font-bold">
          {t.h1}
        </h2>
      </section>

      <main className="max-w-3xl mx-auto py-12 px-6">
        <Helmet>
          <title>{t.title}</title>
          <meta name="description" content={t.desc} />
          <link rel="canonical" href={canonical} />
          <link rel="alternate" hrefLang="en" href={`${origin}/terms`} />
          <link rel="alternate" hrefLang="es" href={`${origin}/terms`} />
          <link rel="alternate" hrefLang="pt" href={`${origin}/terms`} />
          <meta property="og:title" content={t.title} />
          <meta property="og:description" content={t.desc} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={canonical} />
          <meta name="twitter:card" content="summary" />
        </Helmet>

        <p className="text-gray-500 mb-6">{t.updated}</p>

        {t.body.map((p, i) => (
          <p key={i} className="mb-4 text-gray-800 leading-relaxed">{p}</p>
        ))}

        <button
          onClick={() => navigate('/')}
          className="mt-8 px-6 py-2 bg-[#48887B] text-white rounded-lg hover:bg-[#3b726a] transition"
        >
          {language === 'es' ? 'Volver' : language === 'pt' ? 'Voltar' : 'Back'}
        </button>
      </main>
    </>
  );
}
